<template>
  <span class="d-flex description-date-time-range">
    <!-- BeginsAt dateTime (optional "Tagesbeginn/Tagesende") -->
    <time data-test="sdui-list-item-date-time-range-begins-at" :datetime="absence.begins_at">{{ formattedBeginsAtDateTime }}</time>
    <span v-if="dateBeginsAtText" data-test="sdui-list-item-date-time-range-begins-at-text"> {{ dateBeginsAtText }}</span>

    <!-- EndsAt dateTime (optional "Tagesbeginn/Tagesende") -->
    <template v-if="shouldDisplayEndDateTime">
      <span aria-hidden="true"> -</span>
      <span data-test="sdui-list-item-date-time-range-sr-only" class="sr-only"> {{ $t('ui.until') }}</span>

      <time
        data-test="sdui-list-item-date-time-range-ends-at"
        :datetime="!(isBeginEndOnSameDay && isSpanningWholeDays) ? absence.ends_at : undefined"
      >
        {{ formattedEndsAtDateTime }}</time>
      <span v-if="dateEndsAtText" data-test="sdui-list-item-date-time-range-ends-at-text"> {{ dateEndsAtText }}</span>
    </template>
  </span>
</template>

<script>
export default {
  name: 'AbsenceDurationDescription',
  props: {
    absence: {
      type: Object,
      required: true,
    },
  },

  data: () => ({
    localAbsence: undefined,
  }),

  computed: {
    isBeginsAtStartOrEndOfDay() {
      if (!this.localAbsence?.begins_at) return false
      return this.isStartOrEndOfDay(this.$moment(this.localAbsence.begins_at).format('HH:mm'))
    },

    isEndsAtStartOrEndOfDay() {
      if (!this.localAbsence?.ends_at) return false
      return this.isStartOrEndOfDay(this.$moment(this.localAbsence.ends_at).format('HH:mm'))
    },

    dateBeginsAtText() {
      if (!this.localAbsence?.begins_at) return
      if (!this.shouldDisplayEndDateTime && this.isStartOrEndOfDay(this.$moment(this.localAbsence.begins_at).format('HH:mm'))) return
      return this.getEndTextByTime(this.$moment(this.localAbsence?.begins_at).format('HH:mm'))
    },

    dateEndsAtText() {
      if (!this.localAbsence?.ends_at) return
      return this.getEndTextByTime(this.$moment(this.localAbsence.ends_at).format('HH:mm'))
    },

    isBeginEndOnSameDay() {
      if (!this.localAbsence?.begins_at || !this.localAbsence?.ends_at) return false
      return this.$moment(this.localAbsence.begins_at).isSame(this.localAbsence.ends_at, 'day')
    },

    isSpanningWholeDays() {
      return this.isBeginsAtStartOrEndOfDay && this.isEndsAtStartOrEndOfDay
    },

    shouldDisplayEndDateTime() {
      if (!this.localAbsence?.ends_at) return false
      return !(this.isBeginEndOnSameDay && this.isSpanningWholeDays)
    },

    formattedBeginsAtDateTime() {
      return this.getFormattedDateTime(this.localAbsence.begins_at, this.isBeginsAtStartOrEndOfDay ? 'fullDate' : 'dateTime')
    },

    formattedEndsAtDateTime() {
      return this.getFormattedDateTime(this.localAbsence.ends_at, this.isEndsAtStartOrEndOfDay ? 'fullDate' : 'dateTime')
    },
  },

  methods: {
    isStartOrEndOfDay(time) {
      return time === '00:00' || time === '23:59'
    },

    getFormattedDateTime(dateTime, format) {
      if (!dateTime) return
      if (format === 'dateTime') return this.$dateFormat.date_time(dateTime)
      return this.$dateFormat.date(dateTime)
    },

    getEndTextByTime(time) {
      if (time === '00:00') return this.$t('ui.date_time_picker.start_of_day')
      if (time === '23:59') return this.$t('ui.date_time_picker.end_of_day')
      return undefined
    },
  },

  watch: {
    absence: {
      handler(oldVal, newVal) {
        if (this.$compare(oldVal, newVal) && Object.keys(newVal).length) return
        this.localAbsence = this.$clone(this.absence)
      },

      immediate: true,
    },
  },
}
</script>

<style scoped lang="scss">
.description-date-time-range {
  gap: 4px;
}
</style>
